<script setup>
// Auditoria → Resumo: visão consolidada (KPIs + top atores + distribuição por
// ação/entidade + série por dia). Consome `GET /reports/audit_logs/summary`.
//
// Padrão visual: replica `Configurações → Auditoria → Indicadores`
// (SettingsLayout + BaseSettingsHeader + cards encadeados).
import { onMounted, ref, computed } from 'vue';
import subDays from 'date-fns/subDays';
import format from 'date-fns/format';
import AuditLogsAPI from 'dashboard/api/auditLogs';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const isLoading = ref(false);
const data = ref(null);

const customDateRange = ref([subDays(new Date(), 29), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_30_DAYS);

async function fetchSummary() {
  isLoading.value = true;
  try {
    const since = customDateRange.value[0]?.toISOString();
    const until = customDateRange.value[1]?.toISOString();
    const res = await AuditLogsAPI.summary({ since, until });
    data.value = res?.data || null;
  } catch {
    data.value = null;
  } finally {
    isLoading.value = false;
  }
}

function onDateRangeChange(value) {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchSummary();
}

onMounted(fetchSummary);

const totals = computed(() => data.value?.totals || {});
const total = computed(() => totals.value.total || 0);
const byAction = computed(() => Object.entries(totals.value.by_action || {})
  .sort((a, b) => b[1] - a[1]));
const byEntity = computed(() => Object.entries(totals.value.by_entity || {})
  .sort((a, b) => b[1] - a[1]));
const topActors = computed(() => Array.isArray(data.value?.top_actors) ? data.value.top_actors : []);
const seriesByDay = computed(() => {
  const entries = Object.entries(data.value?.series_by_day || {});
  return entries.sort(([a], [b]) => a.localeCompare(b));
});

const peakDay = computed(() => {
  const top = [...seriesByDay.value].sort(([, a], [, b]) => b - a)[0];
  return top ? { date: top[0], count: top[1] } : null;
});

const maxSeriesValue = computed(() => {
  if (!seriesByDay.value.length) return 0;
  return Math.max(...seriesByDay.value.map(([, v]) => v), 1);
});

function formatNumber(v) {
  return Number(v || 0).toLocaleString('pt-BR');
}

function formatDate(iso) {
  try {
    return format(new Date(iso), 'dd/MM');
  } catch {
    return iso;
  }
}

function actionLabel(action) {
  const map = {
    create: 'Criação',
    update: 'Edição',
    destroy: 'Exclusão',
    sign_in: 'Login',
    sign_out: 'Logout',
  };
  return map[action] || action;
}

function pct(value) {
  if (!total.value) return 0;
  return ((value / total.value) * 100).toFixed(1);
}
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="Auditoria · Resumo"
        description="Visão consolidada dos eventos no período: top atores, distribuição por ação e entidade, e série diária."
        feature-name="audit_summary"
      >
        <template #actions>
          <router-link
            :to="{ name: 'auditlogs_list' }"
            class="text-body-small text-n-slate-11 hover:text-n-slate-12 inline-flex items-center gap-1.5"
          >
            <i class="i-lucide-list size-3.5" />
            Ver eventos
          </router-link>
          <router-link
            :to="{ name: 'auditlogs_indicators' }"
            class="text-body-small text-n-slate-11 hover:text-n-slate-12 inline-flex items-center gap-1.5"
          >
            <i class="i-lucide-sliders-horizontal size-3.5" />
            Indicadores
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-4 pb-10">
        <!-- Filtro de período (padrão CSAT: data primeiro, aplica no @change) -->
        <div class="flex flex-col flex-wrap w-full gap-3 md:flex-row md:items-center">
          <WootDatePicker
            v-model:date-range="customDateRange"
            v-model:range-type="selectedDateRange"
            show-range
            @date-range-changed="onDateRangeChange"
          />
          <NextButton
            v-if="isLoading"
            faded
            slate
            size="sm"
            label="Carregando…"
            icon="i-lucide-loader-2"
            disabled
          />
        </div>

        <!-- KPIs -->
        <section class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5">
          <header class="flex items-center gap-2 mb-4">
            <i class="i-lucide-activity size-4 text-n-brand" />
            <h2 class="text-heading-3 text-n-slate-12 mb-0">Indicadores do período</h2>
          </header>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1">Total de eventos</p>
              <p class="text-heading-2 text-n-slate-12 mb-0">{{ formatNumber(total) }}</p>
              <p class="text-body-small text-n-slate-10 mb-0">no intervalo selecionado</p>
            </div>
            <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1">Atores únicos</p>
              <p class="text-heading-2 text-n-slate-12 mb-0">{{ formatNumber(topActors.length) }}</p>
              <p class="text-body-small text-n-slate-10 mb-0">com pelo menos 1 evento</p>
            </div>
            <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1">Entidades tocadas</p>
              <p class="text-heading-2 text-n-slate-12 mb-0">{{ formatNumber(byEntity.length) }}</p>
              <p class="text-body-small text-n-slate-10 mb-0">tipos distintos</p>
            </div>
            <div class="rounded-xl bg-n-alpha-1 px-4 py-3">
              <p class="text-label-small text-n-slate-11 uppercase tracking-wide mb-1">Pico do período</p>
              <p class="text-heading-2 text-n-slate-12 mb-0">
                <template v-if="peakDay">{{ formatNumber(peakDay.count) }}</template>
                <template v-else>-</template>
              </p>
              <p class="text-body-small text-n-slate-10 mb-0">
                <template v-if="peakDay">em {{ formatDate(peakDay.date) }}</template>
                <template v-else>sem dados</template>
              </p>
            </div>
          </div>
        </section>

        <!-- Série diária (barras simples) -->
        <section v-if="seriesByDay.length" class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5">
          <header class="flex items-center gap-2 mb-4">
            <i class="i-lucide-bar-chart-3 size-4 text-n-slate-11" />
            <h2 class="text-heading-3 text-n-slate-12 mb-0">Eventos por dia</h2>
          </header>
          <div class="flex items-end gap-1 h-32">
            <div
              v-for="([day, count]) in seriesByDay"
              :key="day"
              class="flex-1 min-w-0 flex flex-col items-center gap-1"
            >
              <div class="w-full flex flex-col justify-end h-full">
                <div
                  class="w-full rounded-t bg-n-brand transition-all"
                  :style="{ height: `${(count / maxSeriesValue) * 100}%`, minHeight: count > 0 ? '4px' : '0' }"
                  v-tooltip="`${formatDate(day)} · ${count} eventos`"
                />
              </div>
              <span class="text-label-small text-n-slate-10 truncate w-full text-center">
                {{ formatDate(day) }}
              </span>
            </div>
          </div>
        </section>

        <!-- Top atores -->
        <section class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5">
          <header class="flex items-center gap-2 mb-4">
            <i class="i-lucide-users size-4 text-n-slate-11" />
            <h2 class="text-heading-3 text-n-slate-12 mb-0">Top atores</h2>
          </header>
          <ol v-if="topActors.length" class="flex flex-col">
            <li
              v-for="actor in topActors"
              :key="actor.id"
              class="flex items-center justify-between py-2 border-b border-n-weak last:border-b-0"
            >
              <span class="text-body-main text-n-slate-12 truncate">{{ actor.name || 'Sistema' }}</span>
              <div class="flex items-center gap-3">
                <div class="w-32 h-1.5 rounded-full bg-n-alpha-2 overflow-hidden">
                  <div
                    class="h-full bg-n-brand"
                    :style="{ width: `${pct(actor.count)}%` }"
                  />
                </div>
                <span class="text-body-small text-n-slate-11 tabular-nums w-12 text-right">
                  {{ formatNumber(actor.count) }}
                </span>
              </div>
            </li>
          </ol>
          <p v-else class="text-body-small text-n-slate-11 mb-0">
            Nenhum ator registrado no período.
          </p>
        </section>

        <!-- Distribuição por ação + entidade (duas colunas) -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5">
            <header class="flex items-center gap-2 mb-4">
              <i class="i-lucide-zap size-4 text-n-slate-11" />
              <h2 class="text-heading-3 text-n-slate-12 mb-0">Por ação</h2>
            </header>
            <ol v-if="byAction.length" class="flex flex-col">
              <li
                v-for="([action, count]) in byAction"
                :key="`action-${action}`"
                class="flex items-center justify-between py-2 border-b border-n-weak last:border-b-0"
              >
                <span class="text-body-main text-n-slate-12">{{ actionLabel(action) }}</span>
                <div class="flex items-center gap-3">
                  <span class="text-body-small text-n-slate-11 tabular-nums">{{ pct(count) }}%</span>
                  <span class="text-body-small font-semibold text-n-slate-12 tabular-nums w-12 text-right">
                    {{ formatNumber(count) }}
                  </span>
                </div>
              </li>
            </ol>
            <p v-else class="text-body-small text-n-slate-11 mb-0">Sem dados.</p>
          </section>

          <section class="rounded-xl border border-n-weak bg-n-solid-1 px-6 py-5">
            <header class="flex items-center gap-2 mb-4">
              <i class="i-lucide-database size-4 text-n-slate-11" />
              <h2 class="text-heading-3 text-n-slate-12 mb-0">Por entidade</h2>
            </header>
            <ol v-if="byEntity.length" class="flex flex-col">
              <li
                v-for="([entity, count]) in byEntity"
                :key="`entity-${entity}`"
                class="flex items-center justify-between py-2 border-b border-n-weak last:border-b-0"
              >
                <span class="text-body-main text-n-slate-12 truncate">{{ entity }}</span>
                <div class="flex items-center gap-3">
                  <span class="text-body-small text-n-slate-11 tabular-nums">{{ pct(count) }}%</span>
                  <span class="text-body-small font-semibold text-n-slate-12 tabular-nums w-12 text-right">
                    {{ formatNumber(count) }}
                  </span>
                </div>
              </li>
            </ol>
            <p v-else class="text-body-small text-n-slate-11 mb-0">Sem dados.</p>
          </section>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
